import { useQuery } from '@tanstack/react-query';
import { rolesApi } from '@/lib/api';
import type { UserPermissionsResponse } from '@/lib/api';

/**
 * Hook that fetches the current user's permissions from the API.
 * Returns utilities for checking if user has specific permissions.
 *
 * Usage:
 *   const { hasPermission, hasAnyPermission, permissions, role } = usePermissions();
 *   if (hasPermission('project.tasks.create')) { ... }
 */
export function usePermissions() {
  const { data, isLoading } = useQuery<UserPermissionsResponse | null>({
    queryKey: ['myPermissions'],
    queryFn: async () => {
      try {
        return await rolesApi.getMyPermissions();
      } catch {
        return null;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: false,
  });

  const permissions = data?.permissions ?? [];
  const role = data?.role ?? null;

  // Admin has implicit full access to everything
  const isAdminRole = role === 'admin';

  /**
   * Check if the user has a specific permission code.
   * Admin always returns true (full access).
   */
  const hasPermission = (code: string): boolean => {
    if (isAdminRole) return true;
    return permissions.includes(code);
  };

  /**
   * Check if the user has ANY of the given permissions.
   * Admin always returns true.
   */
  const hasAnyPermission = (...codes: string[]): boolean => {
    if (isAdminRole) return true;
    return codes.some((c) => permissions.includes(c));
  };

  /**
   * Check if the user has ALL of the given permissions.
   * Admin always returns true.
   */
  const hasAllPermissions = (...codes: string[]): boolean => {
    if (isAdminRole) return true;
    return codes.every((c) => permissions.includes(c));
  };

  /**
   * Check if user is at least a specific role level.
   */
  const isAtLeast = (minRole: string): boolean => {
    const hierarchy: Record<string, number> = {
      viewer: 0,
      member: 10,
      ba: 20,
      developer: 30,
      admin: 50,
    };
    const userLevel = role ? (hierarchy[role] ?? -1) : -1;
    const minLevel = hierarchy[minRole] ?? 999;
    return userLevel >= minLevel;
  };

  return {
    permissions,
    role,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAtLeast,
    isAdmin: role === 'admin',
  };
}
